import { useState, useEffect } from 'react';
import { MessageSquare, Loader2, CheckCircle, XCircle } from 'lucide-react';
import { supabase } from '../../lib/supabase';
import { useAuth } from '../../contexts/AuthContext';

interface WhatsAppAccount {
  id: string;
  phone_number: string;
  country: string;
  quality: string;
  verified: boolean;
  status: string;
  created_at: string;
}

export function WhatsAppConnectionCard() {
  const { user } = useAuth();
  const [whatsappAccounts, setWhatsappAccounts] = useState<WhatsAppAccount[]>([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    loadWhatsAppAccounts();
  }, [user]);

  const loadWhatsAppAccounts = async () => {
    if (!user?.client_id) return;

    setLoading(true);
    try {
      const { data, error } = await supabase
        .from('whatsapp_accounts')
        .select('*')
        .eq('client_id', user.client_id)
        .eq('is_active', true)
        .order('created_at', { ascending: false });

      if (error) throw error;
      setWhatsappAccounts(data || []);
    } catch (err) {
      console.error('Error loading WhatsApp accounts:', err);
    } finally {
      setLoading(false);
    }
  };

  const getQualityBadge = (quality: string) => {
    const qualityColors = {
      high: 'bg-green-100 text-green-700',
      medium: 'bg-blue-100 text-blue-700',
      low: 'bg-red-100 text-red-700'
    };

    const qualityDotColors = {
      high: 'bg-green-500',
      medium: 'bg-[#1875FF]',
      low: 'bg-red-500'
    };

    const colorClass = qualityColors[quality as keyof typeof qualityColors] || qualityColors.medium;
    const dotColorClass = qualityDotColors[quality as keyof typeof qualityDotColors] || qualityDotColors.medium;

    return (
      <span className={`inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs font-medium ${colorClass}`}>
        <div className={`w-1.5 h-1.5 rounded-full ${dotColorClass}`}></div>
        {quality.charAt(0).toUpperCase() + quality.slice(1)}
      </span>
    );
  };

  const getStatusBadge = (status: string) => {
    const statusColors = {
      connected: 'bg-green-100 text-green-700',
      active: 'bg-green-100 text-green-700',
      disconnected: 'bg-red-100 text-red-700',
      inactive: 'bg-red-100 text-red-700',
      pending: 'bg-blue-100 text-blue-700'
    };

    const statusDotColors = {
      connected: 'bg-green-500',
      active: 'bg-green-500',
      disconnected: 'bg-red-500',
      inactive: 'bg-red-500',
      pending: 'bg-[#1875FF]'
    };

    const colorClass = statusColors[status as keyof typeof statusColors] || statusColors.pending;
    const dotColorClass = statusDotColors[status as keyof typeof statusDotColors] || statusDotColors.pending;

    return (
      <span className={`inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs font-medium ${colorClass}`}>
        <div className={`w-1.5 h-1.5 rounded-full ${dotColorClass}`}></div>
        {status.charAt(0).toUpperCase() + status.slice(1)}
      </span>
    );
  };

  if (loading) {
    return (
      <div className="bg-gradient-to-br from-white to-[#1875FF]/5 rounded-xl border-2 border-[#1875FF]/20 shadow-xl p-12 text-center">
        <Loader2 className="w-8 h-8 text-[#1875FF] animate-spin mx-auto" />
        <p className="text-gray-500 mt-4">Loading WhatsApp accounts...</p>
      </div>
    );
  }

  if (whatsappAccounts.length === 0) {
    return (
      <div className="bg-gradient-to-br from-white to-[#1875FF]/5 rounded-xl border-2 border-[#1875FF]/20 shadow-xl p-12 text-center">
        <div className="w-16 h-16 bg-gradient-to-br from-[#1875FF]/10 to-[#05E0D1]/10 rounded-full flex items-center justify-center mx-auto mb-4">
          <MessageSquare className="w-8 h-8 text-[#1875FF]" />
        </div>
        <h4 className="text-base font-bold text-gray-900 font-display mb-2">No WhatsApp accounts connected</h4>
        <p className="text-sm text-gray-600">
          WhatsApp accounts will be connected through the backend system
        </p>
      </div>
    );
  }

  return (
    <div className="bg-gradient-to-br from-white to-[#1875FF]/5 rounded-xl border-2 border-[#1875FF]/20 shadow-xl overflow-hidden">
      <div className="overflow-x-auto">
        <table className="w-full">
          <thead className="bg-gradient-to-r from-[#05E0D1]/10 to-[#1875FF]/10 border-b-2 border-[#1875FF]/10">
            <tr>
              <th className="text-left px-6 py-4 text-xs font-bold text-gray-700 uppercase tracking-widest font-sans">
                Phone Number
              </th>
              <th className="text-left px-6 py-4 text-xs font-bold text-gray-700 uppercase tracking-widest font-sans">
                Country
              </th>
              <th className="text-left px-6 py-4 text-xs font-bold text-gray-700 uppercase tracking-widest font-sans">
                Quality
              </th>
              <th className="text-left px-6 py-4 text-xs font-bold text-gray-700 uppercase tracking-widest font-sans">
                Verified
              </th>
              <th className="text-left px-6 py-4 text-xs font-bold text-gray-700 uppercase tracking-widest font-sans">
                Status
              </th>
            </tr>
          </thead>
          <tbody className="divide-y divide-gray-100 bg-white/50">
            {whatsappAccounts.map((account) => (
              <tr key={account.id} className="hover:bg-[#1875FF]/5 transition-all duration-200">
                <td className="px-6 py-4">
                  <span className="text-sm font-medium text-gray-900">{account.phone_number}</span>
                </td>
                <td className="px-6 py-4">
                  <span className="text-sm text-gray-700">{account.country}</span>
                </td>
                <td className="px-6 py-4">
                  {getQualityBadge(account.quality)}
                </td>
                <td className="px-6 py-4">
                  <div className="flex items-center gap-2">
                    {account.verified ? (
                      <>
                        <CheckCircle className="w-4 h-4 text-green-600" />
                        <span className="text-sm text-gray-900">Verified</span>
                      </>
                    ) : (
                      <>
                        <XCircle className="w-4 h-4 text-gray-400" />
                        <span className="text-sm text-gray-600">Not verified</span>
                      </>
                    )}
                  </div>
                </td>
                <td className="px-6 py-4">
                  {getStatusBadge(account.status)}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}
